import { Box, Chip, Typography } from "@mui/material";
import { type ElementType, Fragment, ReactNode } from "react";
import { AREA_COLOR_SAWS } from "@/shared/colors";
import { microLabelSx } from "../../weldlines/styles";

export { microLabelSx };

// SAW area accent (teal) + a darker tone for hero values on tinted backgrounds.
export const SAW_ACCENT = AREA_COLOR_SAWS; // #3D8B9E
export const SAW_ACCENT_DARK = "#27606f";

// Shared label for the empty/placeholder state.
export const EMPTY = "—";

// Cards are flex columns that fill their grid cell, so siblings in a row share
// height and the body stretches to use the available space.
export const CardPaperSx = {
	p: 1,
	borderRadius: 2,
	height: "100%",
	display: "flex",
	flexDirection: "column",
} as const;

// Card body below the title — fills the remaining height with one even vertical
// rhythm, so hero / stat strips / lanes are spaced consistently within a card.
export const CardBodySx = {
	flex: 1,
	minHeight: 0,
	display: "flex",
	flexDirection: "column",
	gap: 0.75,
} as const;

type Value = number | string | null | undefined;

const fmt = (value: Value, digits?: number): string => {
	if (value === null || value === undefined || value === "") return EMPTY;
	if (typeof value === "number")
		return digits != null ? value.toFixed(digits) : String(value);
	return value;
};

// Card header — matches the weldline ShiftMetricsCard title (subtitle2 / 700).
// `badge` sits right beside the title; `action` is right-aligned.
export const CardTitle = ({
	title,
	badge,
	action,
}: {
	title: string;
	badge?: ReactNode;
	action?: ReactNode;
}) => (
	<Box
		sx={{
			display: "flex",
			alignItems: "center",
			gap: 0.75,
			mb: 0.4,
			pb: 0.25,
			borderBottom: "1px solid #eee",
			flexShrink: 0,
		}}
	>
		<Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
			{title}
		</Typography>
		{badge}
		<Box sx={{ flexGrow: 1 }} />
		{action}
	</Box>
);

export type InfoGridItem = {
	label: string;
	/** Preformatted value (text or a small node); null renders the standard em dash. */
	value: ReactNode | null;
	/** Take the full row — for long values (e.g. the five QC positions). */
	span?: boolean;
};

// Quiet summary grid: label | value pairs laid out N per row, one type size
// and weight throughout. Structure comes from hairlines only (no box) so the
// card stays the single frame on screen.
export const InfoGrid = ({
	items,
	columns = 2,
}: {
	items: InfoGridItem[];
	columns?: number;
}) => {
	// Assign each item its visual row (span items always start a fresh row) so
	// the last row can drop its hairline regardless of the mix.
	const rowOf: number[] = [];
	let col = 0;
	let row = 0;
	for (const it of items) {
		if (it.span) {
			if (col > 0) {
				row++;
				col = 0;
			}
			rowOf.push(row);
			row++;
		} else {
			rowOf.push(row);
			col++;
			if (col >= columns) {
				row++;
				col = 0;
			}
		}
	}
	const lastRow = rowOf.length > 0 ? rowOf[rowOf.length - 1] : 0;

	return (
		<Box
			sx={{
				px: 0.25,
				display: "grid",
				gridTemplateColumns: `repeat(${columns}, 1fr)`,
				columnGap: 2,
			}}
		>
			{items.map((it, i) => (
				<Box
					key={it.label}
					sx={{
						display: "flex",
						alignItems: "center",
						gap: 0.6,
						minHeight: 24,
						px: 0.25,
						minWidth: 0,
						borderBottom: rowOf[i] === lastRow ? "none" : "1px solid #f1f1f1",
						...(it.span && { gridColumn: "1 / -1" }),
					}}
				>
					<Typography
						sx={{
							fontSize: "0.78rem",
							color: "#5f6b7a",
							whiteSpace: "nowrap",
						}}
					>
						{it.label}
					</Typography>
					<Typography
						component="div"
						sx={{
							ml: "auto",
							fontSize: "0.78rem",
							color: "#1f2937",
							fontVariantNumeric: "tabular-nums",
							whiteSpace: "nowrap",
							overflow: "hidden",
							textOverflow: "ellipsis",
						}}
					>
						{it.value ?? EMPTY}
					</Typography>
				</Box>
			))}
		</Box>
	);
};

// Outlined micro chip for bundle state (Checked / Cutback / In progress ...).
// Tablet label/value pair: caps label over a bold value.
export const capsSx = {
	fontSize: 11,
	fontWeight: 700,
	letterSpacing: 0.6,
	textTransform: "uppercase",
	color: "text.secondary",
	whiteSpace: "nowrap",
} as const;
export const valueSx = {
	fontSize: 17,
	fontWeight: 700,
	fontVariantNumeric: "tabular-nums",
	whiteSpace: "nowrap",
} as const;

// StateChip's outline with an icon inside; filled = solid color, white text.
export const IconChip = ({
	label,
	color,
	Icon,
	filled,
	title,
}: {
	label: string;
	color: string;
	Icon?: ElementType;
	filled?: boolean;
	title?: string;
}) => (
	<Box
		component="span"
		title={title}
		sx={{
			display: "inline-flex",
			alignItems: "center",
			gap: 0.4,
			px: 0.75,
			py: 0.2,
			borderRadius: 0.75,
			border: "1px solid",
			borderColor: color,
			color: filled ? "#fff" : color,
			bgcolor: filled ? color : "transparent",
			fontSize: 12,
			fontWeight: 700,
			whiteSpace: "nowrap",
			flexShrink: 0,
		}}
	>
		{Icon && <Icon sx={{ fontSize: 15 }} />}
		{label}
	</Box>
);

export const StateChip = ({
	label,
	color,
}: {
	label: string;
	color: string;
}) => (
	<Box
		sx={{
			px: 1,
			py: 0.35,
			borderRadius: 0.75,
			border: "1px solid",
			borderColor: color,
			color,
			fontSize: "0.58rem",
			fontWeight: 700,
			textTransform: "uppercase",
			letterSpacing: "0.05em",
			textAlign: "center",
			flexShrink: 0,
		}}
	>
		{label}
	</Box>
);

// Small teal chip for hero metadata (work order, stack type, ...).
export const SawChip = ({
	label,
	accent = SAW_ACCENT,
	accentDark = SAW_ACCENT_DARK,
}: {
	label: ReactNode;
	accent?: string;
	accentDark?: string;
}) => (
	<Chip
		size="small"
		label={label}
		sx={{
			height: 20,
			fontSize: "0.66rem",
			fontWeight: 700,
			letterSpacing: 0.2,
			color: accentDark,
			backgroundColor: `${accent}1A`,
			border: `1px solid ${accent}55`,
			"& .MuiChip-label": { px: 0.7 },
		}}
	/>
);

// Marks a card or a section as "no data yet": desaturates + fades the content
// under a light frosted veil with a centered badge. Data-driven — pass `active`
// (e.g. value == null) and it disappears on its own once the field is wired.
// Use `fill` when wrapping a whole card so it keeps stretching to the grid cell;
// omit it for an inline section that should keep its natural height.
export const Unavailable = ({
	active,
	label = "No data available",
	fill = false,
	children,
}: {
	active: boolean;
	label?: string;
	fill?: boolean;
	children: ReactNode;
}) => {
	if (!active) return <>{children}</>;
	return (
		<Box sx={{ position: "relative", ...(fill && { height: "100%" }) }}>
			<Box
				aria-hidden
				sx={{
					...(fill && { height: "100%" }),
					filter: "grayscale(1)",
					opacity: 0.62,
					pointerEvents: "none",
					userSelect: "none",
				}}
			>
				{children}
			</Box>
			<Box
				sx={{
					position: "absolute",
					inset: 0,
					display: "flex",
					alignItems: "flex-start",
					justifyContent: "flex-end",
					p: 0.5,
					borderRadius: 2,
					backgroundColor: "rgba(249, 250, 251, 0.22)",
					pointerEvents: "none",
				}}
			>
				<Box
					sx={{
						px: 1,
						py: 0.45,
						borderRadius: 1,
						display: "flex",
						alignItems: "center",
						gap: 0.6,
						backgroundColor: "rgba(255, 255, 255, 0.92)",
						border: "1px dashed #c4ccd4",
						boxShadow: "0 1px 3px rgba(0, 0, 0, 0.06)",
					}}
				>
					<Box
						sx={{
							width: 7,
							height: 7,
							borderRadius: "50%",
							backgroundColor: "#aab2bb",
						}}
					/>
					<Typography
						sx={{
							fontSize: "0.64rem",
							fontWeight: 700,
							color: "#828a93",
							letterSpacing: 0.5,
							textTransform: "uppercase",
							whiteSpace: "nowrap",
						}}
					>
						{label}
					</Typography>
				</Box>
			</Box>
		</Box>
	);
};

// Amber accent for the "next" card (same pair the overview notes banner uses).
export const NEXT_ACCENT = "#F2B600";
export const NEXT_ACCENT_DARK = "#7a5700";

export type IdentityRow = {
	label: string;
	value: Value;
	/** Small node right before the value (a chip, a "scanned at" caption). */
	aside?: ReactNode;
};

// The card's identity block: one or more quiet rows sharing one type size,
// with only an accent edge for identity — the card chip lives in the title.
export const IdentityRows = ({
	accent,
	rows,
}: {
	accent: string;
	rows: IdentityRow[];
}) => (
	<Box
		sx={{
			display: "flex",
			flexDirection: "column",
			borderLeft: `3px solid ${accent}`,
			pl: 1,
			flexShrink: 0,
		}}
	>
		{rows.map((row, i) => (
			<Fragment key={row.label}>
				{i > 0 && <Box sx={{ borderTop: "1px solid #f1f1f1" }} />}
				<Box
					sx={{
						display: "flex",
						alignItems: "center",
						gap: 0.75,
						minHeight: 22,
					}}
				>
					<Typography sx={{ ...microLabelSx, whiteSpace: "nowrap" }}>
						{row.label}
					</Typography>
					<Box sx={{ flexGrow: 1 }} />
					{row.aside}
					<Typography
						sx={{
							fontSize: "0.9rem",
							fontWeight: 700,
							color: "#212121",
							whiteSpace: "nowrap",
							overflow: "hidden",
							textOverflow: "ellipsis",
							fontVariantNumeric: "tabular-nums",
							minWidth: 0,
						}}
						title={fmt(row.value)}
					>
						{fmt(row.value)}
					</Typography>
				</Box>
			</Fragment>
		))}
	</Box>
);

// Part # / Ext. Part # — the product cards' identity pair.
export const PartPairRows = ({
	accent,
	part,
	ext,
}: {
	accent: string;
	part: Value;
	ext: Value;
}) => (
	<IdentityRows
		accent={accent}
		rows={[
			{ label: "Part #", value: part },
			{ label: "Ext. Part #", value: ext },
		]}
	/>
);

// Quiet one-line stat strip: label/value pairs with dot separators, no box.
export const InlineStats = ({
	items,
}: {
	items: {
		label: string;
		value: Value;
		highlight?: boolean;
		// Judged value: green pill when it complies, red when it doesn't.
		pill?: "ok" | "bad";
	}[];
}) => (
	<Box
		sx={{
			display: "flex",
			alignItems: "center",
			justifyContent: "space-between",
			gap: 1,
			px: 0.5,
			borderTop: "1px solid #f1f1f1",
			borderBottom: "1px solid #f1f1f1",
			minHeight: 26,
			flexShrink: 0,
		}}
	>
		{items.map((it, i) => (
			<Fragment key={it.label}>
				{i > 0 && (
					<Box component="span" sx={{ color: "#bdbdbd" }}>
						·
					</Box>
				)}
				<Box sx={{ whiteSpace: "nowrap", minWidth: 0 }}>
					<Typography
						component="span"
						sx={{ ...microLabelSx, whiteSpace: "nowrap" }}
					>
						{it.label}
					</Typography>{" "}
					<Typography
						component="span"
						sx={{
							fontSize: "0.85rem",
							fontWeight: it.highlight || it.pill ? 800 : 700,
							color: it.pill
								? it.pill === "ok"
									? "#1b5e20"
									: "#b71c1c"
								: it.highlight
									? "#1b5e20"
									: "#37474f",
							fontVariantNumeric: "tabular-nums",
							...(it.pill && {
								backgroundColor: it.pill === "ok" ? "#e8f5e9" : "#ffebee",
								borderRadius: 0.75,
								px: 0.6,
								py: 0.1,
							}),
						}}
					>
						{fmt(it.value)}
					</Typography>
				</Box>
			</Fragment>
		))}
	</Box>
);
